import Vue from 'vue'
import { get, cloneDeep } from 'lodash'
import { formatTemplate } from '../utils/handlebars'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advanced from 'dayjs/plugin/advancedFormat'

dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(advanced)

const translateFilters = (filter) => {
  switch (filter) {
  case 'notEquals':
    return 'ne'
  case 'equals':
    return 'eq'
  case 'contains':
    return 'contains'
  case 'notContains':
    return 'notcontains'
  case 'startsWith':
    return 'startwith'
  case 'endsWith':
    return 'endswith'
  case 'lessThan':
    return 'lt'
  case 'lessThanOrEqual':
    return 'lte'
  case 'greaterThan':
    return 'gt'
  case 'greaterThanOrEqual':
    return 'gte'
  case 'blank':
    return 'empty'
  default:
    return 'eq'
  }
}

export const formatFilterFields = (columns, filter) => {
  const result = columns.reduce((acc, column) => {
    const getFilter = filter[column.field]

    if (column.filter && getFilter) {
      const filterGroup = column?.filterGroup || `filters`

      if (!acc.hasOwnProperty(filterGroup)) {
        acc[filterGroup] = []
      }

      if (getFilter.filter) {
        acc[filterGroup].push({
          op: 'OR',
          conditions: [
            {
              field: column.field,
              op: translateFilters(getFilter.type),
              value: getFilter.filter
            }
          ]
        })
      }
      else if (getFilter.filterType === 'set') {
        if (column.filterIsCondition) {
          acc[filterGroup].push({
            op: 'AND',
            conditions: getFilter.values.map((value) => {
              return {
                field: column.field,
                op: 'eq',
                value: value
              }
            })
          })
        }
        else {
          acc[filterGroup].push(...getFilter.values)
        }
      }
      else {
        if (getFilter.filterType === 'date' && !getFilter.operator) {
          if (getFilter.type === 'inRange') {
            const dateFrom = dayjs(getFilter.dateFrom).tz().format()
            const dateTo = dayjs(getFilter.dateTo).tz().format()

            const dateFromField = column.rangeFilterFields?.dateFrom
            const dateToField = column.rangeFilterFields?.dateTo

            if (dateFromField) {
              acc[dateFromField] = dateFrom
            }
            if (dateToField) {
              acc[dateToField] = dateTo
            }
          }
        }
        else {
          acc[filterGroup].push({
            op: getFilter.operator,
            conditions: [
              {
                field: column.field,
                op: translateFilters(
                  getFilter.condition1.type
                ),
                value: getFilter.condition1.filter
              },
              {
                field: column.field,
                op: translateFilters(
                  getFilter.condition2.type
                ),
                value: getFilter.condition2.filter
              }
            ]
          })
        }
      }
    }
    return acc
  }, { filters: [] })

  return Object.keys(result).reduce((acc, k) => {
    const value = result[k]
    if (value.length) {
      acc[k] = value
    }
    return acc
  }, {})
}

export const formatSortFields = (columns, sortModel) => {
  if (sortModel.length) {
    return columns.reduce((acc, column) => {
      if (sortModel[0].colId === column.field) {
        if (column.sortField) {
          acc.push({
            field: column.sortField,
            order: sortModel[0].sort
          })
        }
        else if (column.field) {
          acc.push({
            field: column.field,
            order: sortModel[0].sort
          })
        }
      }
      return acc
    }, [])
  }
  else {
    return []
  }
}

export const formatExportData = (columns, data, queriesData) => {
  return data.map(item => {
    return columns.reduce((acc, column) => {
      acc[column.colDef.field] = cellRendererDisplay({
        type: column.colDef.cellRenderer,
        data: item,
        colDef: column.colDef,
        value: item[column.colDef.field],
        queriesData
      })

      return acc
    }, {})
  })
}

export const getPathValue = (data, path, excludeValues = []) => {
  const getValue = get(data, path, null)
  if (getValue !== undefined && getValue !== null && !excludeValues.includes(getValue)) {
    return getValue
  }
}

export const cellRendererDisplay = ({ type, data, colDef, queriesData, global, rowNumber }) => {
  let value = get(data, colDef.field)
  let display = ''
  const path = colDef.path
  const excludeValues = colDef.excludeValues || []
  const separator = colDef.separator || ''
  const template = colDef.template

  const defaultTemplateContext = cloneDeep({
    global: global,
    preQuery: queriesData
  })

  const values = []
  if (path) {
    if (typeof path === 'string') {
      const value = getPathValue(data, path, excludeValues)
      values.push(value)
    }
    else if (Array.isArray(path)) {
      path.forEach((p) => {
        const value = getPathValue(data, p, excludeValues)
        values.push(value)
      })
    }
    value = values.filter(_ => _).join(separator)
  }

  if (type === 'enumRenderer') {
    const items = get(defaultTemplateContext, colDef.itemsPath, [])
    const itemLabel = colDef.itemLabel || 'label'
    const itemValue = colDef.itemValue || 'value'
    const itemDefault = colDef.itemDefault

    if (items && Array.isArray(items) && items.length) {
      let getItem = items.find(item => item[itemValue] === value)
      if (itemDefault && !getItem) {
        getItem = items.find(item => item[itemValue] === value)
      }
      value = getItem[itemValue]
      display = getItem[itemLabel]
    }
    else if (items && typeof items === 'object') {
      let getItem = items[value]
      if (itemDefault && !getItem) {
        getItem = items[itemDefault]
      }
      display = getItem
    }
    else {
      display = value
    }
  }
  else if (type === 'numberRenderer') {
    const prefix = colDef.numberPrefix
    const precision = colDef.numberPrecision
    const rounding = colDef.numberRounding

    if (value) {
      let formattedValue = +value || 0

      if (rounding === 'up') {
        formattedValue = Math.ceil(formattedValue)
      }
      else if (rounding === 'down') {
        formattedValue = Math.floor(formattedValue)
      }

      if (precision) {
        formattedValue = formattedValue.toFixed(precision)
      }

      display = `${prefix}${formattedValue}`
    }
  }
  else if (type === 'dateRenderer') {
    let dateFormat = colDef.dateFormat || 'DD MMM YYYY'
    if (value) {
      const date = dayjs(value)
      display = date.format(dateFormat)
    }
  }
  else if (type === 'booleanRenderer') {
    const trueValue = colDef.trueValue || 'Yes'
    const falseValue = colDef.falseValue || 'No'
    const unsetValue = colDef.unsetValue || 'Unset'

    if (typeof value === 'boolean') {
      display = value ? trueValue : falseValue
    }
    else {
      display = unsetValue
    }
  }
  else if (type === 'addressRenderer') {
    const fieldBlock = [['street1', 'street2'], ['city', 'state', 'country', 'postcode']]
    const separator = colDef.separator || ', '
    const addressBlock = colDef.addressBlock

    const [getValues] = values

    if (getValues) {
      const extractedAddress = fieldBlock.reduce((acc, fields) => {
        const getFields = []
        fields.forEach((field) => {
          const fieldValue = getValues[field]
          if (fieldValue) {
            getFields.push(fieldValue)
          }
        })

        if (getFields.length) {
          acc.push(getFields.join(separator))
        }

        return acc
      }, [])

      if (addressBlock) {
        display = extractedAddress.join('</br>')
      }
      else {
        display = extractedAddress.join(separator)
      }
    }
  }
  else {
    display = value
  }

  if (template && template.length) {
    return formatTemplate(template, { ...defaultTemplateContext, display, value, row: data, rowNumber })
  }
  else {
    return display
  }
}

export const formatCSV = (columns, content) => {
  const columnOrder = []
  const headers = columns.reduce((acc, item) => {
    if (item.colDef.cellRenderer !== 'actionsRenderer') {
      columnOrder.push(item.colDef.field)
      acc.push(`"${item.colDef.headerName}"`)
    }
    return acc
  }, [])

  const csvText = [headers.join(',')]

  content.forEach(item => {
    const row = []
    columnOrder.forEach(col => {
      row.push(`"${item[col] || ''}"`)
    })
    csvText.push(row.join(','))
  })

  return csvText.join('\n')
}

export const createElement = (component, params) => {
  const Component = Vue.extend({
    render(createElement) {
      return createElement(component, params)
    }
  })
  const instance = new Component({
    el: document.createElement('div')
  })

  const $el = instance.$el
  instance.$destroy()
  return $el
}
